import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../components/mainLayout";
import axios from "axios";
import { showError, showSuccess } from "../utils/toast";
import "../sass/pages/transferenciaLicenca.scss";

const ERP_BASE_URL = "https://apisantsystemweb30git-production-318e.up.railway.app/erp";

function normStr(v) {
  return String(v ?? "").trim();
}

function readStorage(keys = []) {
  for (const k of keys) {
    const v = sessionStorage.getItem(k) || localStorage.getItem(k);
    if (v && String(v).trim() !== "") return v;
  }
  return "";
}

const TransferenciaLicenca = () => {
  const [origem, setOrigem] = useState(null);
  const [destino, setDestino] = useState(null);

  const [qOrigem, setQOrigem] = useState("");
  const [qDestino, setQDestino] = useState("");

  const [optsOrigem, setOptsOrigem] = useState([]);
  const [optsDestino, setOptsDestino] = useState([]);

  const [loadingOrigem, setLoadingOrigem] = useState(false);
  const [loadingDestino, setLoadingDestino] = useState(false);

  const [openOrigem, setOpenOrigem] = useState(false);
  const [openDestino, setOpenDestino] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [executando, setExecutando] = useState(false);

  const apiErp = useMemo(() => {
    const instance = axios.create({
      baseURL: ERP_BASE_URL, // ✅ bate direto no /erp
      timeout: 30000,
      headers: { "Content-Type": "application/json" },
    });

    // Se tiver token no seu sistema, injeta aqui (sem depender do ../api)
    instance.interceptors.request.use((config) => {
      const token = readStorage(["token", "accessToken", "jwt", "ssw_token"]);
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    return instance;
  }, []);

  const idUsuario = useMemo(() => {
    const v = readStorage(["idUsuario", "ID_USUARIO", "codFunc"]);
    const n = Number(v);
    return Number.isFinite(n) ? n : 1;
  }, []);

  // ====================== API ======================
  const buscarLicencas = async (q) => {
    const query = normStr(q);

    const params = {
      q: query || undefined,
      limit: 30,
      // situacao: "A", // se quiser só ativas
    };

    // baseURL já é /erp, então aqui é /licenca
    const { data } = await apiErp.get("/licenca", { params });
    return Array.isArray(data) ? data : [];
  };

  // ====================== EFEITOS (debounce) ======================
  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        setLoadingOrigem(true);
        const rows = await buscarLicencas(qOrigem);
        setOptsOrigem(rows);
      } catch (err) {
        console.error("[Licenca] buscar origem erro:", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Falha ao buscar licenças (origem).";
        showError(msg);
      } finally {
        setLoadingOrigem(false);
      }
    }, 300);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qOrigem]);

  useEffect(() => {
    const handle = setTimeout(async () => {
      try {
        setLoadingDestino(true);
        const rows = await buscarLicencas(qDestino);
        setOptsDestino(rows);
      } catch (err) {
        console.error("[Licenca] buscar destino erro:", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Falha ao buscar licenças (destino).";
        showError(msg);
      } finally {
        setLoadingDestino(false);
      }
    }, 300);

    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDestino]);

  useEffect(() => {
    (async () => {
      try {
        setLoadingOrigem(true);
        setLoadingDestino(true);

        const [a, b] = await Promise.all([buscarLicencas(""), buscarLicencas("")]);
        setOptsOrigem(a);
        setOptsDestino(b);
      } catch (err) {
        console.error("[Licenca] inicial erro:", err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Falha ao carregar licenças.";
        showError(msg);
      } finally {
        setLoadingOrigem(false);
        setLoadingDestino(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====================== AÇÕES ======================
  const limpar = () => {
    setOrigem(null);
    setDestino(null);
    setQOrigem("");
    setQDestino("");
    setOpenOrigem(false);
    setOpenDestino(false);
  };

  const trocar = () => {
    const a = origem;
    const b = destino;
    setOrigem(b);
    setDestino(a);
  };

  const podeTransferir = () => {
    if (!origem?.NR_LICENCA) return false;
    if (!destino?.NR_LICENCA) return false;
    if (normStr(origem.NR_LICENCA) === normStr(destino.NR_LICENCA)) return false;
    return true;
  };

  const abrirConfirmacao = () => {
    if (!podeTransferir()) {
      showError("Selecione origem e destino válidos (diferentes).");
      return;
    }
    setModalOpen(true);
  };

  const executarTransferencia = async () => {
    if (!podeTransferir()) return;

    try {
      setExecutando(true);

      const payload = {
        nrLicencaOrigem: normStr(origem.NR_LICENCA),
        nrLicencaDestino: normStr(destino.NR_LICENCA),
        idUsuario,
      };

      // baseURL já é /erp, então aqui é /transfer/licenca
      const { data } = await apiErp.post("/transfer/licenca", payload);

      showSuccess("Transferência executada com sucesso.");
      console.log("[Transfer] result:", data);

      setModalOpen(false);
    } catch (err) {
      console.error("[Transfer] erro:", err);

      const status = err?.response?.status;
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Falha ao executar transferência.";

      // 409 é o bloqueio esperado
      if (status === 409) {
        showError(msg);
      } else {
        showError(msg);
      }
    } finally {
      setExecutando(false);
    }
  };

  // ====================== RENDER ======================
  return (
    <MainLayout>
      <div
        className="transfer-page"
        onClick={() => {
          setOpenOrigem(false);
          setOpenDestino(false);
        }}
      >
        <header className="transfer-header">
          <div>
            <h1>Transferência de Licença</h1>
            <p className="transfer-subtitle">
              Seleciona licença de origem e destino e executa a transferência de cadastros.
            </p>
          </div>

          <div className="transfer-header-actions">
            <button type="button" className="btn btn-secondary" onClick={limpar}>
              Limpar
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={abrirConfirmacao}
              disabled={!podeTransferir()}
            >
              Transferir
            </button>
          </div>
        </header>

        <section className="transfer-cards">
          {/* ORIGEM */}
          <div className="card">
            <div className="card-head">
              <h2>Origem</h2>
              <span className="pill">{origem?.NR_LICENCA || "-"}</span>
            </div>

            <div className="select-wrap" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                placeholder="Buscar por nome ou NR_LICENCA..."
                value={qOrigem}
                onChange={(e) => {
                  setQOrigem(e.target.value);
                  setOpenOrigem(true);
                }}
                onFocus={() => setOpenOrigem(true)}
              />

              {openOrigem && (
                <div className="dropdown">
                  <div className="dropdown-info">
                    {loadingOrigem ? "Carregando..." : `${optsOrigem.length} resultados`}
                  </div>

                  {optsOrigem.length === 0 && !loadingOrigem && (
                    <div className="dropdown-empty">Nenhuma licença encontrada.</div>
                  )}

                  {optsOrigem.map((l) => (
                    <button
                      type="button"
                      key={`${l.NR_LICENCA}-${l.NM_FANTASIA}`}
                      className={
                        origem?.NR_LICENCA === l.NR_LICENCA
                          ? "dropdown-item selected"
                          : "dropdown-item"
                      }
                      onClick={() => {
                        setOrigem(l);
                        setOpenOrigem(false);
                      }}
                    >
                      <div className="item-main">{l.NM_FANTASIA}</div>
                      <div className="item-sub">
                        <span className="mono">{l.NR_LICENCA}</span>
                        <span className="sep">•</span>
                        <span>{l.DM_SITUACAO || "-"}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="card-foot">
              <div className="kv">
                <span className="k">Fantasia</span>
                <span className="v">{origem?.NM_FANTASIA || "-"}</span>
              </div>
              <div className="kv">
                <span className="k">Tipo</span>
                <span className="v">{origem?.DM_TIPO_SISTEMA || "-"}</span>
              </div>
              <div className="kv">
                <span className="k">Armazenamento</span>
                <span className="v">{origem?.TIPO_ARMAZENAMENTO || "-"}</span>
              </div>
            </div>
          </div>

          {/* AÇÕES CENTRAIS */}
          <div className="mid-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={trocar}
              disabled={!origem && !destino}
            >
              ⇄ Trocar
            </button>
            <div className="hint">Destino deve estar sem registros (bloqueio automático).</div>
          </div>

          {/* DESTINO */}
          <div className="card">
            <div className="card-head">
              <h2>Destino</h2>
              <span className="pill">{destino?.NR_LICENCA || "-"}</span>
            </div>

            <div className="select-wrap" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                placeholder="Buscar por nome ou NR_LICENCA..."
                value={qDestino}
                onChange={(e) => {
                  setQDestino(e.target.value);
                  setOpenDestino(true);
                }}
                onFocus={() => setOpenDestino(true)}
              />

              {openDestino && (
                <div className="dropdown">
                  <div className="dropdown-info">
                    {loadingDestino ? "Carregando..." : `${optsDestino.length} resultados`}
                  </div>

                  {optsDestino.length === 0 && !loadingDestino && (
                    <div className="dropdown-empty">Nenhuma licença encontrada.</div>
                  )}

                  {optsDestino.map((l) => (
                    <button
                      type="button"
                      key={`${l.NR_LICENCA}-${l.NM_FANTASIA}`}
                      className={
                        destino?.NR_LICENCA === l.NR_LICENCA
                          ? "dropdown-item selected"
                          : "dropdown-item"
                      }
                      onClick={() => {
                        setDestino(l);
                        setOpenDestino(false);
                      }}
                    >
                      <div className="item-main">{l.NM_FANTASIA}</div>
                      <div className="item-sub">
                        <span className="mono">{l.NR_LICENCA}</span>
                        <span className="sep">•</span>
                        <span>{l.DM_SITUACAO || "-"}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="card-foot">
              <div className="kv">
                <span className="k">Fantasia</span>
                <span className="v">{destino?.NM_FANTASIA || "-"}</span>
              </div>
              <div className="kv">
                <span className="k">Tipo</span>
                <span className="v">{destino?.DM_TIPO_SISTEMA || "-"}</span>
              </div>
              <div className="kv">
                <span className="k">Armazenamento</span>
                <span className="v">{destino?.TIPO_ARMAZENAMENTO || "-"}</span>
              </div>
            </div>
          </div>
        </section>

        {/* MODAL CONFIRMAÇÃO */}
        {modalOpen && (
          <div
            className="transfer-modal-backdrop"
            onClick={() => (!executando ? setModalOpen(false) : null)}
          >
            <div
              className="transfer-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="transfer-modal-header">
                <h2>Confirmar transferência</h2>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setModalOpen(false)}
                  aria-label="Fechar"
                  disabled={executando}
                >
                  ×
                </button>
              </div>

              <div className="transfer-modal-body">
                <div className="confirm-grid">
                  <div className="box">
                    <div className="t">Origem</div>
                    <div className="b">{origem?.NM_FANTASIA || "-"}</div>
                    <div className="s mono">{origem?.NR_LICENCA || "-"}</div>
                  </div>
                  <div className="arrow">→</div>
                  <div className="box">
                    <div className="t">Destino</div>
                    <div className="b">{destino?.NM_FANTASIA || "-"}</div>
                    <div className="s mono">{destino?.NR_LICENCA || "-"}</div>
                  </div>
                </div>

                <div className="warn">
                  Operação crítica. Se o destino possuir qualquer registro, o backend bloqueará com 409 para evitar
                  sobrescrita.
                </div>
              </div>

              <div className="transfer-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setModalOpen(false)}
                  disabled={executando}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={executarTransferencia}
                  disabled={executando}
                >
                  {executando ? "Executando..." : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default TransferenciaLicenca;
